import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Flag, Loader2, ExternalLink } from "lucide-react";

const REASON_FR = {
  offre_expiree: "Offre expirée",
  reduction_incorrecte: "Réduction incorrecte",
  contenu_incorrect: "Contenu incorrect",
  probleme_partenaire: "Problème avec le partenaire",
  autre: "Autre",
};
const STATUSES = ["nouveau", "en_cours", "traite"];
const STATUS_FR = {
  nouveau: { label: "Nouveau", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  en_cours: { label: "En cours", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  traite: { label: "Traité", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function AdminOfferReports() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/offer-reports", { params: filter ? { status: filter } : {} });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/offer-reports/${id}`, { status });
      toast.success("Signalement mis à jour.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-offer-reports-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Flag className="text-[#E05638]" size={26} /> Offres signalées
      </h1>
      <div className="mt-4 flex gap-2">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? "bg-slate-900 text-white" : "bg-white"}`} data-testid="admin-reports-filter-all">Tous</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === s ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`admin-reports-filter-${s}`}>{STATUS_FR[s].label}</button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="p-8 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm">Aucun signalement.</div>
        ) : items.map((r) => {
          const st = STATUS_FR[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
          return (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {r.offer_title || "Offre"}
                    <Link to={`/offre/${r.offer_id}`} target="_blank" className="text-slate-400 hover:text-[#E05638]"><ExternalLink size={14} /></Link>
                  </div>
                  <div className="text-xs text-slate-400">{REASON_FR[r.reason] || r.reason} · {(r.created_at || "").slice(0, 16).replace("T", " ")}{r.reporter_email ? ` · ${r.reporter_email}` : ""}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${st.cls}`}>{st.label}</span>
              </div>
              {r.detail && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.detail}</p>}
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => setStatus(r.id, "en_cours")}>En cours</Button>
                <Button size="sm" disabled={busy === r.id} onClick={() => setStatus(r.id, "traite")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-report-resolve-${r.id}`}>Marquer traité</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
